import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useMutation } from "@apollo/client/react";
// @ts-ignore
import { load } from "@cashfreepayments/cashfree-js";
import {
  BOOK_EVENT,
  CREATE_EVENT_PAYMENT_ORDER,
} from "../graphql/mutation";
import type { EventItem } from "../interfaces/event.interface";
import { isEventOfferCurrentlyActive } from "../utils/offerPricing";
import TruncatedTitle from "./TruncatedTitle";

type SnackbarType = "success" | "error" | "info";

// Extended type for event items returned from the API (includes computed offer fields)
interface EventItemWithOffer extends EventItem {
  isOfferActive?: boolean;
  offerPrice?: number | null;
  promoCode?: string;
  offerEndDate?: string;
}

interface EventPurchaseDrawerProps {
  open: boolean;
  event: EventItemWithOffer | null;
  onClose: () => void;
  onPurchaseComplete: (message: string, type: SnackbarType) => void;
  returnPath?: string;
  instructorNames?: string;
  isBooked?: boolean;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(amount);

const normalizePayableAmount = (amount: number) => {
  if (amount <= 0) return 0;
  return Math.max(1, Number(amount.toFixed(2)));
};

const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatTime = (timeRange: string) => {
  if (!timeRange) return "";
  const fmt = (t: string) => {
    const match = t.trim().match(/^(\d+):(\d+)$/);
    if (!match) return t.trim();
    let h = parseInt(match[1]);
    const m = match[2];
    const period = h >= 12 ? "PM" : "AM";
    if (h > 12) h -= 12;
    if (h === 0) h = 12;
    return `${h}:${m} ${period}`;
  };
  if (timeRange.includes("-")) {
    const [start, end] = timeRange.split("-");
    return `${fmt(start)} - ${fmt(end)}`;
  }
  return fmt(timeRange);
};

const EventPurchaseDrawer: React.FC<EventPurchaseDrawerProps> = ({
  open,
  event,
  onClose,
  onPurchaseComplete,
  returnPath = "/events",
  instructorNames,
  isBooked,
}) => {
  const [status, setStatus] = useState<{ type: SnackbarType; message: string } | null>(null);
  const [processing, setProcessing] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);

  const [bookEvent] = useMutation(BOOK_EVENT);
  const [createEventPaymentOrder] = useMutation(CREATE_EVENT_PAYMENT_ORDER);

  useEffect(() => {
    if (open) {
      setStatus(null);
      setProcessing(false);
      setDescExpanded(false);
    }
  }, [open, event?.id]);

  const hasActiveOffer = useMemo(
    () => Boolean(event && isEventOfferCurrentlyActive(event)),
    [event]
  );

  const finalPrice = useMemo(() => {
    if (!event) return 0;
    const base = hasActiveOffer ? (event as any).offerPrice : event.standardPrice;
    return normalizePayableAmount(base || 0);
  }, [event, hasActiveOffer]);

  const isFreeEvent = finalPrice <= 0;

  const discountPct = useMemo(() => {
    if (!hasActiveOffer || !event || event.standardPrice <= 0) return 0;
    return Math.round(
      ((event.standardPrice - (event as any).offerPrice) / event.standardPrice) * 100
    );
  }, [event, hasActiveOffer]);

  const handlePayment = async () => {
    if (!event) return;
    setProcessing(true);
    setStatus({
      type: "info",
      message: isFreeEvent ? "Confirming your free booking..." : "Preparing your secure checkout...",
    });

    try {
      if (isFreeEvent) {
        const { data } = await bookEvent({ variables: { eventId: event.id } });
        if (data?.bookEvent?.success) {
          const message = data.bookEvent.message || "Event booked successfully!";
          setStatus({ type: "success", message });
          onPurchaseComplete(message, "success");
          return;
        }
        const errorMessage = data?.bookEvent?.message || "Failed to book event";
        setStatus({ type: "error", message: errorMessage });
        onPurchaseComplete(errorMessage, "error");
        return;
      }

      const { data: orderRes } = await createEventPaymentOrder({
        variables: {
          eventId: event.id,
          finalPrice,
          promoCode: hasActiveOffer ? event.promoCode : undefined,
          returnUrl: `${window.location.origin}/payment/callback?source=event&redirect=${encodeURIComponent(returnPath)}&order_id={order_id}`,
        },
      });

      if (!orderRes?.createEventPaymentOrder?.success) {
        const errorMessage =
          orderRes?.createEventPaymentOrder?.message || "Failed to initialize payment";
        setStatus({ type: "error", message: errorMessage });
        onPurchaseComplete(errorMessage, "error");
        return;
      }

      const { paymentSessionId, cfMode } = orderRes.createEventPaymentOrder;
      const cashfree = await load({ mode: cfMode || "sandbox" });

      if (!cashfree) throw new Error("Cashfree SDK failed to load");

      setStatus({ type: "info", message: "Redirecting you to the payment page..." });
      await cashfree.checkout({ paymentSessionId, redirectTarget: "_self" });
    } catch {
      const errorMessage = "An error occurred. Please try again.";
      setStatus({ type: "error", message: errorMessage });
      onPurchaseComplete(errorMessage, "error");
    } finally {
      setProcessing(false);
    }
  };

  const thumbnailSrc = event?.thumbnail
    ? event.thumbnail.startsWith("data:") || event.thumbnail.startsWith("http")
      ? event.thumbnail
      : `data:image/*;base64,${event.thumbnail}`
    : null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => {
        if (!processing) onClose();
      }}
      PaperProps={{
        sx: {
          width: { xs: "100vw", sm: 440 },
          background: "linear-gradient(180deg, #FFFFFF 0%, #FCFDFB 52%, #F5F8F3 100%)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderBottom: "1px solid #EDF2EA",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
            Complete your booking
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280", mt: 0.25 }}>
            {isFreeEvent
              ? "Review the event details and confirm your free booking."
              : "Review your event and continue with secure payment."}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          disabled={processing}
          size="small"
          sx={{ border: "1px solid #E5E7EB", borderRadius: "10px", color: "#6B7280" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {status && (
        <Box
          sx={{
            mx: { xs: 2, sm: 3 },
            mt: 1.5,
            px: 1.5,
            py: 1.1,
            borderRadius: "14px",
            border: "1px solid",
            borderColor:
              status.type === "success"
                ? "#CBE8D0"
                : status.type === "error"
                  ? "#F4C7C7"
                  : "#D7E4F2",
            background:
              status.type === "success"
                ? "linear-gradient(180deg, #F4FBF5 0%, #EAF8ED 100%)"
                : status.type === "error"
                  ? "linear-gradient(180deg, #FFF5F5 0%, #FDECEC 100%)"
                  : "linear-gradient(180deg, #F4F8FD 0%, #EDF5FF 100%)",
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color:
                status.type === "success"
                  ? "#2E7D32"
                  : status.type === "error"
                    ? "#C62828"
                    : "#1D4ED8",
            }}
          >
            {status.message}
          </Typography>
        </Box>
      )}

      {/* Scrollable body */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2, sm: 3 },
          pt: 1,
          pb: 2.5,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {event && (
          <Stack spacing={1.5} sx={{ pb: 1 }}>
            <Box
              sx={{
                borderRadius: "24px",
                overflow: "hidden",
                position: "relative",
                minHeight: "200px",
                background: thumbnailSrc
                  ? "none"
                  : "linear-gradient(135deg, #0F172A 0%, #1F7A3F 48%, #A4D65E 100%)",
              }}
            >
              {thumbnailSrc && (
                <Box
                  component="img"
                  src={thumbnailSrc}
                  alt={event.eventName}
                  sx={{ width: "100%", height: "220px", objectFit: "cover", display: "block" }}
                />
              )}
              {/* Gradient overlay */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: "70%",
                  background: "linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0) 100%)",
                }}
              />
              {!thumbnailSrc && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -24,
                    right: -28,
                    width: 110,
                    height: 110,
                    borderRadius: "50%",
                    backgroundColor: "rgba(255,255,255,0.12)",
                  }}
                />
              )}
              {/* Text overlay */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  p: { xs: 2, sm: 2.5 },
                  color: "#fff",
                  zIndex: 1,
                }}
              >
                <TruncatedTitle
                  text={event.eventName}
                  sx={{
                    fontSize: 24,
                    fontWeight: 800,
                    lineHeight: 1.15,
                    color: "#fff",
                  }}
                />
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
                  {event.location && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.4,
                        px: 1.25,
                        py: 0.6,
                        borderRadius: "999px",
                        backgroundColor: "rgba(255,255,255,0.18)",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      <LocationOnOutlinedIcon sx={{ fontSize: 13 }} />
                      {event.location}
                    </Box>
                  )}
                  {event.time && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.4,
                        px: 1.25,
                        py: 0.6,
                        borderRadius: "999px",
                        backgroundColor: "rgba(255,255,255,0.18)",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      <AccessTimeOutlinedIcon sx={{ fontSize: 13 }} />
                      {formatTime(event.time)}
                    </Box>
                  )}
                </Stack>
              </Box>
            </Box>

            {/* Details + Payment card */}
            <Box
              sx={{
                borderRadius: "20px",
                backgroundColor: "#FFFFFF",
                border: "1px solid #E8EEE3",
                boxShadow: "0px 12px 30px rgba(17, 24, 39, 0.06)",
                overflow: "hidden",
              }}
            >
              {/* Event details section */}
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 800,
                    color: "#6B7280",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                  }}
                >
                  Event details
                </Typography>
                <Stack spacing={1.25} sx={{ mt: 1.75 }}>
                  {event.startDate && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <CalendarTodayOutlinedIcon sx={{ fontSize: 15, color: "#6B7280" }} />
                        <Typography sx={{ color: "#6B7280", fontSize: 14 }}>Date</Typography>
                      </Box>
                      <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700, textAlign: "right" }}>
                        {formatDate(event.startDate)}
                        {event.endDate && event.endDate !== event.startDate && ` → ${formatDate(event.endDate)}`}
                      </Typography>
                    </Box>
                  )}
                  {event.time && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <AccessTimeOutlinedIcon sx={{ fontSize: 15, color: "#6B7280" }} />
                        <Typography sx={{ color: "#6B7280", fontSize: 14 }}>Time</Typography>
                      </Box>
                      <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700, textAlign: "right" }}>
                        {formatTime(event.time)}
                      </Typography>
                    </Box>
                  )}
                  {event.location && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <LocationOnOutlinedIcon sx={{ fontSize: 15, color: "#6B7280" }} />
                        <Typography sx={{ color: "#6B7280", fontSize: 14 }}>Location</Typography>
                      </Box>
                      <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700, textAlign: "right", maxWidth: 220 }}>
                        {event.location}
                      </Typography>
                    </Box>
                  )}
                  {instructorNames && (
                    <Box display="flex" justifyContent="space-between" gap={2}>
                      <Typography sx={{ color: "#6B7280", fontSize: 14, flexShrink: 0 }}>Instructor</Typography>
                      <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700, textAlign: "right", maxWidth: 220 }}>
                        {instructorNames}
                      </Typography>
                    </Box>
                  )}
                  {event.duration && (
                    <Box display="flex" justifyContent="space-between" gap={2}>
                      <Typography sx={{ color: "#6B7280", fontSize: 14 }}>Duration</Typography>
                      <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700 }}>
                        {event.duration}
                      </Typography>
                    </Box>
                  )}
                  {event.description && (
                    <Box display="flex" flexDirection="column" gap={0.5} sx={{ mt: 1 }}>
                      <Typography sx={{ color: "#6B7280", fontSize: 14 }}>Description</Typography>
                      <Box>
                        <Typography
                          sx={{
                            color: "#111827",
                            fontSize: 14,
                            fontWeight: 500,
                            display: descExpanded ? "block" : "-webkit-box",
                            WebkitLineClamp: descExpanded ? "none" : 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {event.description}
                        </Typography>
                        {event.description.length > 80 && (
                          <Box
                            component="span"
                            onClick={() => setDescExpanded(!descExpanded)}
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              color: "#6BC242",
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: "pointer",
                              mt: 0.5,
                              "&:hover": { textDecoration: "underline" },
                            }}
                          >
                            {descExpanded ? "Read less" : "Read more"}
                            {descExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </Stack>
              </Box>
              <Divider />
              <Box sx={{ px: 2.5, py: 2.25, backgroundColor: "#FBFCFA" }}>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 800,
                    color: "#6B7280",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                  }}
                >
                  {isFreeEvent ? "Booking details" : "Payment details"}
                </Typography>

                <Stack spacing={1.2} sx={{ mt: 1.75 }}>
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Typography sx={{ color: "#6B7280", fontSize: 14 }}>
                      {isFreeEvent ? "Booking price" : "Original price"}
                    </Typography>
                    <Typography sx={{ color: "#111827", fontSize: 14, fontWeight: 700 }}>
                      {event.standardPrice === 0 ? "Free" : formatCurrency(event.standardPrice)}
                    </Typography>
                  </Box>

                  {hasActiveOffer && (
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.75,
                          backgroundColor: "#F0FBE8",
                          border: "1px solid #C6ECA8",
                          borderRadius: "999px",
                          px: 1.25,
                          py: 0.5,
                        }}
                      >
                        <LocalOfferOutlinedIcon sx={{ fontSize: 14, color: "#5AA839" }} />
                        <Typography sx={{ color: "#5AA839", fontSize: 12, fontWeight: 800, textTransform: "uppercase" }}>
                          {discountPct}% off
                        </Typography>
                      </Box>
                      <Typography sx={{ color: "#5AA839", fontSize: 14, fontWeight: 800 }}>
                        -{formatCurrency(event.standardPrice - (event as any).offerPrice)}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ borderColor: "#E5E7EB" }} />

                  <Box display="flex" justifyContent="space-between" alignItems="flex-end" gap={2}>
                    <Box>
                      <Typography sx={{ color: "#6B7280", fontSize: 13 }}>
                        {isFreeEvent ? "Amount to confirm" : "Amount payable"}
                      </Typography>
                      <Typography sx={{ color: "#111827", fontSize: 28, fontWeight: 900, lineHeight: 1.2, mt: 0.4, pb: 0.2 }}>
                        {finalPrice === 0 ? "Free" : formatCurrency(finalPrice)}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>
        )}
      </Box>

      {/* Footer CTA */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: "1px solid #E8EEE3",
          backgroundColor: "rgba(255,255,255,0.96)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Button
          fullWidth
          variant="contained"
          onClick={handlePayment}
          disabled={!event || processing || isBooked}
          endIcon={!processing && !isBooked ? <ArrowForwardIcon sx={{ fontSize: 24 }} /> : undefined}
          sx={{
            backgroundColor: isBooked ? "#E8F5E9" : "#6BC242",
            color: isBooked ? "#2E7D32" : "#fff",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 15,
            borderRadius: "14px",
            py: 1.6,
            minHeight: 56,
            boxShadow: "none",
            letterSpacing: "0.01em",
            "&:hover": { backgroundColor: isBooked ? "#E8F5E9" : "#62B63C" },
            "&:disabled": { color: isBooked ? "#2E7D32" : "#fff", background: isBooked ? "#E8F5E9" : "#B7D9A8" },
            "& .MuiButton-endIcon": { ml: 1 },
          }}
        >
          {processing ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} sx={{ color: isBooked ? "#2E7D32" : "#fff" }} />
              Processing...
            </Box>
          ) : isBooked ? (
            "Joined"
          ) : finalPrice <= 0 ? (
            "Book Free Event"
          ) : (
            `Pay ${formatCurrency(finalPrice)}`
          )}
        </Button>
      </Box>
    </Drawer>
  );
};

export default EventPurchaseDrawer;
